import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Search, X, UserPlus, Loader2 } from 'lucide-react';
import { FetchUsers } from '../../../../api/General/account';
import { getLeaveTypes, getBalancePerUser } from '../../../../api/Attendance/leaves';
import { LeaveBalanceView, LeaveTypeView } from '../../../../models/attendance';
import { UserView } from '../../../../models/general';
import { getFullUrl } from '../../../../utils/url';

interface OnBehalfEmployeePickerProps {
  userId: string;
  company: { id: number };
  onSelect: (employee: { userId: string; fullName: string; balances: LeaveBalanceView[]; leaveTypes: LeaveTypeView[] }) => void;
  onCancel: () => void;
}

const OnBehalfEmployeePicker: React.FC<OnBehalfEmployeePickerProps> = ({
  userId,
  company,
  onSelect,
  onCancel,
}) => {
  const { t } = useTranslation(['leaves', 'common']);

  const [employees, setEmployees] = useState<UserView[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingEmployee, setLoadingEmployee] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingList(true);
      try {
        const result = await FetchUsers('Company', company.id);
        if (!cancelled && result.errors.length === 0) {
          // Exclude current manager from the list
          setEmployees(result.payload.filter((u: UserView) => u.id !== userId));
        }
      } catch { /* ignore */ }
      if (!cancelled) setLoadingList(false);
    };
    load();
    return () => { cancelled = true; };
  }, [company.id, userId]);

  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(
      (e) =>
        e.fullName?.toLowerCase().includes(term) ||
        e.email?.toLowerCase().includes(term) ||
        e.title?.toLowerCase().includes(term)
    );
  }, [employees, searchTerm]);

  const handleSelectEmployee = async (employee: UserView) => {
    setLoadingEmployee(employee.id);
    try {
      const [typesRes, balancesRes] = await Promise.all([
        getLeaveTypes(employee.id),
        getBalancePerUser(employee.id),
      ]);

      const leaveTypes = typesRes.errors.length === 0 ? typesRes.payload : [];
      const balances = balancesRes.errors.length === 0 ? balancesRes.payload : [];

      onSelect({
        userId: employee.id,
        fullName: employee.fullName || employee.userName,
        balances,
        leaveTypes,
      });
    } catch {
      // If data loading fails, still allow selection with empty data
      onSelect({
        userId: employee.id,
        fullName: employee.fullName || employee.userName,
        balances: [],
        leaveTypes: [],
      });
    }
    setLoadingEmployee(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <UserPlus size={20} color="white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('Submit on Behalf')}</h3>
                <p className="text-sm text-muted-foreground">{t('onBehalfDescription')}</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              data-testid="on-behalf-close-btn"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <Search size={18} className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('selectEmployeePlaceholder')}
              data-testid="on-behalf-search"
              autoFocus
              className="w-full ltr:pl-10 ltr:pr-4 rtl:pr-10 rtl:pl-4 py-2.5 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
          </div>
        </div>

        {/* Employee List */}
        <div className="flex-1 overflow-y-auto p-2">
          {loadingList ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground text-sm">
              {t('No employees found')}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredEmployees.map((employee) => (
                <button
                  key={employee.id}
                  onClick={() => handleSelectEmployee(employee)}
                  disabled={loadingEmployee !== null}
                  data-testid={`on-behalf-employee-${employee.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors text-start disabled:opacity-50"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0 text-white font-medium text-sm">
                    {getFullUrl(employee.imageUrl) ? (
                      <img
                        src={getFullUrl(employee.imageUrl)!}
                        alt={employee.fullName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      (employee.fullName || employee.userName || '?').charAt(0).toUpperCase()
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">
                      {employee.fullName || employee.userName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {employee.title || employee.role}
                      {employee.email && ` · ${employee.email}`}
                    </div>
                  </div>

                  {/* Loading indicator for selected employee */}
                  {loadingEmployee === employee.id && (
                    <Loader2 size={18} className="animate-spin text-blue-500 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <button
            type="button"
            onClick={onCancel}
            data-testid="on-behalf-cancel-btn"
            className="w-full px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-lg transition-colors"
          >
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  company: state.user.company,
});

export default connect(mapStateToProps)(OnBehalfEmployeePicker);
